import React, { CSSProperties } from 'react';

import t from '../../../labels/labelling-utils';
import { makeClassNames } from '../../../utils/styles';
import { Label, Text } from '../../atoms';
import { NewCommentsBadge, SimplePagination } from '../../molecules';
import { IMatchComment } from '../../organisms/match-comment/match-comment';
import MatchCommentsTimeline from '../../organisms/match-comments-timeline/match-comments-timeline';
import JSONLD from '../../../context/jsonld/jsonld';

import './match-comments.scss';

interface IUpdatesBadge {
  count: number; // Is the count to show for NEW COMMENTS since the BADGE was DISMISSED
  isVisible: boolean;
  onClick?: () => void;
}

const fulltimes = ['FullTime', 'FullTimeAfterOverTime', 'FullTimeAfterShootOut'];

export interface IRichString {
  seoName: string;
  customName: string;
}

export interface IMatchComments {
  comments?: IMatchComment[];
  firstCommentDate: string;
  lastCommentDate: string;
  competition: IRichString;
  homeTeam: IRichString;
  awayTeam: IRichString;
  matchStatus: string;
  url: string;
  isLive?: boolean;
  newPostsCount?: number; // Is the count to show for ALL NEW COMMENTS since page was RELODED
  nextLink?: string;
  prevLink?: string;
  updatesBadge?: IUpdatesBadge;
  trackingData?: {
    prev: object;
    next: object;
  };
}

interface IMatchCommentsClasses {
  matchComments: string;
  newCommentsCounter: string;
}

const context = 'http://schema.org';
const publisherName = 'Sky Sport';
const publisherUrl = 'https://sport.sky.it/';
const publisherLogoUrl = 'https://static.sky.it/images/skysport/nst/logo-skysport.png';
const authorName = 'Redazione Sky Sport';

const getEndOfCurrentDate = () => {
  const now = new Date();
  const endOfDayUTC = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 23, 59, 59, 999));
  return endOfDayUTC.toISOString();
};

const getWordCount = (props: { text: string }) => {
  const { text } = props;

  return text.split(' ').length;
};

export const classNames = (): IMatchCommentsClasses =>
  makeClassNames({
    matchComments: 'match-comments',
    newCommentsCounter: 'match-comments__counter',
  }) as IMatchCommentsClasses;

const MatchComments = ({
  firstCommentDate,
  lastCommentDate,
  competition,
  homeTeam,
  awayTeam,
  matchStatus,
  url,
  isLive = false,
  prevLink,
  nextLink,
  comments = [],
  updatesBadge,
  newPostsCount = 0,
  trackingData,
}: IMatchComments) => {
  const cn = classNames();
  const { count = 0, isVisible, onClick: onClickBadge } = updatesBadge || {};

  const liveBlogPosting = {
    '@context': context,
    '@type': 'LiveBlogPosting',
    coverageStartTime: firstCommentDate,
    coverageEndTime: fulltimes.includes(matchStatus) ? lastCommentDate : getEndOfCurrentDate(),
    dateModified: lastCommentDate,
    datePublished: firstCommentDate,
    publisher: {
      '@type': 'Organization',
      name: publisherName,
      url: publisherUrl,
      logo: {
        '@type': 'ImageObject',
        url: publisherLogoUrl,
      },
    },
    author: {
      '@type': 'Organization',
      name: authorName,
      url: publisherUrl,
    },
    description: publisherName,
    headline: `${homeTeam.customName} ${awayTeam.customName}: minuto per minuto`,
    url,
    about: {
      type: 'Thing',
      name: competition.customName,
    },
    liveBlogUpdate: comments.map((comment) => ({
      '@type': 'BlogPosting',
      articleBody: comment.message,
      datePublished: comment.createdAt,
      dateModified: comment.updatedAt,
      author: {
        '@type': 'Organization',
        name: authorName,
        url: publisherUrl,
      },
      publisher: {
        '@type': 'Organization',
        name: authorName,
        url: publisherUrl,
        logo: {
          '@type': 'ImageObject',
          url: publisherLogoUrl,
        },
      },
      image: 'null',
      mainEntityOfPage: url,
      url: `${url}#${comment.commentId}`,
      '@id': comment.commentId,
      wordCount: comment.message ? getWordCount({ text: comment.message }) : 0,
      sharedContent: '',
    })),
  };

  const newsArticle = {
    '@context': context,
    '@type': 'NewsArticle',
    headline: `${homeTeam.customName} ${awayTeam.customName}: minuto per minuto`,
    url,
    image: {
      '@type': 'ImageObject',
      url: '',
    },
    thumbnailurl: '',
    dateCreated: firstCommentDate,
    articleSection: competition.customName,
    keywords: [homeTeam.seoName, awayTeam.seoName, competition.seoName],
    mainEntityOfPage: url,
    datePublished: firstCommentDate,
    dateModified: lastCommentDate,
    author: {
      '@type': 'Person',
      name: authorName,
    },
    creator: {
      '@type': 'Thing',
      name: authorName,
    },
    publisher: {
      '@type': 'Organization',
      name: publisherName,
      logo: {
        '@context': context,
        '@type': 'ImageObject',
        url: publisherLogoUrl,
      },
    },
  };

  return (
    <div
      className={cn.matchComments}
      style={{ '--lvbg-color-light': '#fff', '--lvbg-new-comments-badge-background': '#051045' } as CSSProperties}
      data-analytics-element="widget|live-opta"
    >
      <div id="match-comments-top" />
      {newPostsCount > 0 && (
        <Text tag="p" color="red-light" fontWeight="700" fontSize="18" className={cn.newCommentsCounter}>
          {newPostsCount} {newPostsCount === 1 ? t('new.comments.title.singular') : t('new.comments.title.plural')}
        </Text>
      )}
      {isLive && <Label label={t('match.status.live')} isLive isFat />}
      {comments?.length > 0 && <MatchCommentsTimeline comments={comments} newPostsCount={newPostsCount + 1 || 1} />}
      <SimplePagination trackingData={trackingData} prevLink={prevLink} nextLink={nextLink} />
      <NewCommentsBadge isShown={isVisible} count={count} onClick={onClickBadge} />
      <JSONLD json={[liveBlogPosting, newsArticle]} dangerouslyExposeHtml />
    </div>
  );
};

export default MatchComments;
